/* CaseNotesTab — iter97.23
 *
 * Unified, chronological notes feed for a case. Surfaces every note that
 * was authored against the case from every role that's allowed to see it.
 * Replaces the floating "Add note" buttons that were scattered across the
 * production case detail page in iter97.19.
 *
 * Sources (newest first):
 *   • Admin assignment notes (production_assignment.note)
 *   • Production tech notes (production_stage_history is_note=true)
 *   • Actionable timeline events (hold reasons, escalation reasons,
 *     rework reasons, materials log substitution+wastage reasons,
 *     unlock reasons, ship notes, doctor rebuttals)
 *
 * Authoring: production / coe_lead / admin / qa can post a note via
 * AddProductionNoteDialog. Posts get appended live to the local feed
 * before refetching from server.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Plus, Note, ShieldCheck, Crown, ChatTeardropDots, Clock, MagnifyingGlass,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import AddProductionNoteDialog from "@/components/production/AddProductionNoteDialog";


const SOURCE_META = {
    admin_assignment: {
        label: "Admin · routed to production",
        Icon: Crown,
        tone: "violet",
    },
    production_tech: {
        label: "Production tech",
        Icon: ShieldCheck,
        tone: "primary",
    },
    timeline: {
        label: "Activity",
        Icon: ChatTeardropDots,
        tone: "slate",
    },
};

const TONE_CLS = {
    violet:  { ring: "border-violet-200 bg-violet-50/30",  icon: "bg-violet-100 text-violet-700",  chip: "bg-violet-100 text-violet-800 border-violet-200" },
    primary: { ring: "border-primary/20 bg-primary/5",     icon: "bg-primary/10 text-primary",     chip: "bg-primary/10 text-primary border-primary/20" },
    slate:   { ring: "border-border bg-card",              icon: "bg-secondary text-foreground",   chip: "bg-secondary text-foreground border-border" },
};


export default function CaseNotesTab({ caseObj, onChange }) {
    const caseId = caseObj?.id;
    const currentStageLabel = caseObj?.production_stage
        ? caseObj.production_stage.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
        : "";
    const { user } = useAuth();
    const role = user?.role;
    const staff = user?.staff_type;
    const canPost = role === "admin" || ["production", "coe_lead", "qa"].includes(staff);

    const [items, setItems] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState("");
    const [sourceFilter, setSourceFilter] = useState("all");
    const [noteOpen, setNoteOpen] = useState(false);

    const load = async () => {
        if (!caseId) return;
        setLoading(true);
        try {
            const r = await api.get(`/cases/${caseId}/notes-feed`);
            setItems(r.data?.items || []);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.detail || "Failed to load notes");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [caseId]);

    const filtered = useMemo(() => {
        if (!items) return [];
        let pool = items;
        if (sourceFilter !== "all") {
            pool = pool.filter((i) => i.source === sourceFilter);
        }
        if (query.trim()) {
            const q = query.trim().toLowerCase();
            pool = pool.filter((i) =>
                (i.text || "").toLowerCase().includes(q) ||
                (i.author_name || "").toLowerCase().includes(q) ||
                (i.source_label || "").toLowerCase().includes(q),
            );
        }
        return pool;
    }, [items, query, sourceFilter]);

    const counts = useMemo(() => {
        const c = { all: items?.length || 0, admin_assignment: 0, production_tech: 0, timeline: 0 };
        for (const i of (items || [])) c[i.source] = (c[i.source] || 0) + 1;
        return c;
    }, [items]);

    if (!caseId) return null;

    return (
        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="case-notes-tab">
            <div className="px-5 py-3 border-b border-border flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="font-display font-semibold text-sm inline-flex items-center gap-2">
                        <Note size={16} weight="duotone" className="text-primary" /> Notes
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                        Every note authored against this case — admin routing, production handoffs, hold + escalation reasons, materials substitutions, and more.
                    </div>
                </div>
                {canPost && (
                    <Button
                        size="sm"
                        onClick={() => setNoteOpen(true)}
                        className="rounded-full"
                        data-testid="case-notes-add"
                    >
                        <Plus size={12} weight="bold" className="mr-1" /> Add note
                    </Button>
                )}
            </div>

            {/* Filter bar */}
            <div className="px-5 py-2.5 border-b border-border bg-secondary/20 flex items-center gap-2 flex-wrap">
                <div className="inline-flex bg-card rounded-full p-1 border border-border">
                    {[
                        { k: "all",                 label: "All"     },
                        { k: "production_tech",     label: "Tech"    },
                        { k: "admin_assignment",    label: "Admin"   },
                        { k: "timeline",            label: "Activity" },
                    ].map((f) => (
                        <button
                            key={f.k}
                            onClick={() => setSourceFilter(f.k)}
                            disabled={f.k !== "all" && (counts[f.k] || 0) === 0}
                            className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                                sourceFilter === f.k
                                    ? "bg-primary text-white"
                                    : "text-muted-foreground hover:text-foreground"
                            } disabled:opacity-40 disabled:cursor-not-allowed`}
                            data-testid={`case-notes-filter-${f.k}`}
                        >
                            {f.label}
                            {counts[f.k] > 0 && (
                                <span className={`ml-1 ${sourceFilter === f.k ? "opacity-90" : "opacity-60"}`}>· {counts[f.k]}</span>
                            )}
                        </button>
                    ))}
                </div>
                <div className="relative flex-1 max-w-xs ml-auto">
                    <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder="Search notes…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        className="h-8 pl-8 text-xs"
                        data-testid="case-notes-search"
                    />
                </div>
            </div>

            {/* Feed body */}
            <div className="p-4 space-y-2.5 bg-secondary/10 min-h-[200px]" data-testid="case-notes-feed">
                {loading && (
                    <div className="text-xs text-muted-foreground italic py-6 text-center">Loading notes…</div>
                )}
                {!loading && error && (
                    <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                        {error}
                    </div>
                )}
                {!loading && !error && filtered.length === 0 && (
                    <EmptyState canPost={canPost} hasItems={!!items?.length} onAdd={() => setNoteOpen(true)} />
                )}
                {!loading && !error && filtered.map((n) => (
                    <NoteCard key={n.id} note={n} />
                ))}
            </div>

            {canPost && (
                <AddProductionNoteDialog
                    open={noteOpen}
                    caseId={caseId}
                    currentStageLabel={currentStageLabel}
                    onClose={() => setNoteOpen(false)}
                    onAdded={() => { setNoteOpen(false); load(); onChange?.(); }}
                />
            )}
        </div>
    );
}


function NoteCard({ note }) {
    const meta = SOURCE_META[note.source] || SOURCE_META.timeline;
    const tone = TONE_CLS[meta.tone] || TONE_CLS.slate;
    const SourceIcon = meta.Icon;
    const dt = note.created_at ? new Date(note.created_at) : null;
    const dtLabel = dt
        ? dt.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })
        : "—";
    const relative = dt ? relativeFromNow(dt) : "";
    return (
        <div
            className={`rounded-xl border ${tone.ring} p-3 flex gap-3`}
            data-testid={`case-notes-item-${note.id}`}
            data-source={note.source}
        >
            <div className={`w-8 h-8 rounded-lg ${tone.icon} flex items-center justify-center shrink-0`}>
                <SourceIcon size={14} weight="duotone" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`text-[10px] font-bold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded border ${tone.chip}`}>
                        {note.source_label || meta.label}
                    </span>
                    <span className="text-xs font-semibold text-foreground" data-testid={`case-notes-author-${note.id}`}>
                        {note.author_name || "Unknown"}
                    </span>
                    {note.context && (
                        <span className="text-[10px] text-muted-foreground italic truncate max-w-[200px]" title={note.context}>
                            · {note.context}
                        </span>
                    )}
                    <span
                        className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5 ml-auto shrink-0"
                        title={dt ? dt.toLocaleString("en-IN") : ""}
                    >
                        <Clock size={10} weight="bold" /> {relative || dtLabel}
                    </span>
                </div>
                <div className="text-sm text-foreground/90 leading-snug whitespace-pre-wrap" data-testid={`case-notes-text-${note.id}`}>
                    {note.text}
                </div>
            </div>
        </div>
    );
}


function EmptyState({ canPost, hasItems, onAdd }) {
    return (
        <div className="rounded-xl border border-dashed border-border bg-card px-6 py-10 text-center" data-testid="case-notes-empty">
            <div className="w-12 h-12 mx-auto rounded-full bg-secondary/60 flex items-center justify-center mb-2">
                <Note size={22} weight="duotone" className="text-muted-foreground" />
            </div>
            <div className="font-semibold text-foreground text-sm">
                {hasItems ? "No notes match this filter" : "No notes yet"}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1 max-w-md mx-auto">
                {hasItems
                    ? "Clear the search or pick a different source filter to see all notes."
                    : "Notes will appear here when admin routes a case, when production techs flag shift handoffs, or when actions like holds and escalations are captured."}
            </div>
            {canPost && !hasItems && (
                <button
                    onClick={onAdd}
                    className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                    data-testid="case-notes-empty-add"
                >
                    <Plus size={11} weight="bold" /> Add the first note
                </button>
            )}
        </div>
    );
}


function relativeFromNow(d) {
    const diffMs = Date.now() - d.getTime();
    if (diffMs < 0) return "";
    const m = Math.floor(diffMs / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const days = Math.floor(h / 24);
    if (days < 7) return `${days}d ago`;
    return "";
}
